import React from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth-config';

const StudentNavbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuthContext();

  const isVideoPlayerLoaded = [
    '/courses/:bookId/:videoId',
    '/courses/:bookId'
  ].some((path) => matchPath({ path, end: true }, location.pathname));

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const firstName = auth.user?.firstName || 'Masti';
  const lastName = auth.user?.lastName || 'Manch';
  const initials = ((firstName[0] || '') + (lastName[0] || '')).toUpperCase() || 'MM';

  return (
    <header className="h-16 px-6 flex items-center justify-between border-b border-[#d1d7dc] sticky top-0 bg-white z-[100]">
      {/* Left: Logo and Explore */}
      <div className="flex items-center gap-4 flex-1">
        <Link to="/dashboard" className="w-24" data-purpose="logo">
          <img 
            alt="Masti Manch Logo" 
            className="h-8" 
            src="/images/mmlogo.png"
          />
        </Link>
        <button className="text-sm font-normal hover:text-[#5624d0]">Explore</button>
      </div>

      {/* Center: Search Bar */}
      <div className="flex-[2] max-w-2xl px-4">
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-[#6a6f73]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
            </svg>
          </div>
          <input 
            className="block w-full pl-10 pr-3 py-2 border border-[#2d2f31] rounded-full bg-[#f7f9fa] text-sm focus:bg-white focus:outline-none focus:ring-0" 
            placeholder="Search for anything" 
            type="text"
          />
        </div>
      </div>

      {/* Right: User Nav */}
      <div className="flex items-center gap-4 flex-1 justify-end">
        <Link className="text-sm hover:text-[#5624d0] text-[#2d2f31]" to="/dashboard">My learning</Link>
        {isVideoPlayerLoaded && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('mm-chat-open-event'))}
            className="p-2 text-[#2d2f31] hover:text-[#5624d0] transition-colors"
            title="Ask MastiManch AI"
            aria-label="Open MastiManch AI chat"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        )}
        <button className="relative p-2 text-[#2d2f31]">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
          </svg>
          <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
        </button>

        {/* Profile Avatar & Dropdown */}
        <div className="relative group">
          <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold text-xs cursor-pointer relative">
            {initials}
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-purple-500 border-2 border-white rounded-full"></span>
          </div>
          
          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-64 rounded-md shadow-lg border border-gray-200 bg-white opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="p-4 border-b border-gray-200">
              <p className="font-bold text-sm text-[#2d2f31]">
                {firstName} {lastName}
              </p>
            </div>
            <div className="py-1">
              <Link to="/profile" className="block px-4 py-2 hover:bg-gray-100 text-sm font-semibold text-[#2d2f31]">Profile Settings</Link>
              <Link to="/progress" className="block px-4 py-2 hover:bg-gray-100 text-sm font-semibold text-[#2d2f31]">Progress Report</Link>
              <div className="border-t border-gray-200 my-1"></div>
              <button 
                onClick={handleLogout}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-100 flex items-center space-x-2 text-sm font-semibold text-[#2d2f31]"
              >
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default StudentNavbar;
